// HTTP client for the backend. One place for base URL, API key, session cookie + CSRF header,
// timeouts and the error envelope, so features never parse raw responses.

export const API_BASE: string = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';
const KEY_STORAGE = 'pde.apiKey';
const DEFAULT_TIMEOUT_MS = 60_000;

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string = 'error',
    public readonly details: Record<string, unknown> = {},
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** True when the backend could not be reached at all (offline, down, timed out). */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

// The API key lives in sessionStorage: it survives reloads within a tab but is not
// shared across tabs or persisted after the browser closes.
export function getApiKey(): string {
  try { return sessionStorage.getItem(KEY_STORAGE) ?? ''; } catch { return ''; }
}
export function setApiKey(key: string): void {
  try {
    if (key) sessionStorage.setItem(KEY_STORAGE, key);
    else sessionStorage.removeItem(KEY_STORAGE);
  } catch { /* storage unavailable (private mode): key stays in memory only for this call */ }
}

interface Envelope { error?: { code?: string; message?: string; details?: Record<string, unknown>; request_id?: string } }

function parseError(status: number, body: unknown, requestId?: string): ApiError {
  const env = (body && typeof body === 'object' ? body : {}) as Envelope;
  const e = env.error;
  if (e?.message) return new ApiError(e.message, status, e.code ?? 'error', e.details ?? {}, e.request_id ?? requestId);
  return new ApiError(`Request failed (${status}).`, status, 'http_error', {}, requestId);
}

// Signed-in sessions use an HttpOnly cookie set by the backend. State-changing requests must echo
// the CSRF token (double-submit): it is readable from the pde_csrf cookie, or set after sign-in.
const CSRF_COOKIE = 'pde_csrf';
let csrfToken = '';
export function setCsrfToken(token: string): void { csrfToken = token; }
function currentCsrf(): string {
  // The cookie is authoritative (the server rotates it on every sign-in); memory is the fallback
  // for browsers that hide cookies from scripts.
  const match = typeof document !== 'undefined' ? document.cookie.split('; ').find((c) => c.startsWith(`${CSRF_COOKIE}=`)) : undefined;
  return match ? decodeURIComponent(match.slice(CSRF_COOKIE.length + 1)) : csrfToken;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  raw?: boolean; // return the Response (for file downloads)
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  if (!path.startsWith('/') || path.startsWith('//')) throw new ApiError('Invalid API path.', 0, 'client_error');
  const headers = new Headers({ Accept: opts.raw ? '*/*' : 'application/json' });
  if (opts.body !== undefined) headers.set('Content-Type', 'application/json');
  const key = getApiKey();
  if (key) headers.set('X-API-Key', key);
  const method = opts.method ?? 'GET';
  if (method !== 'GET') {
    const csrf = currentCsrf();
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }

  const timeout = AbortSignal.timeout(opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const signal = opts.signal ? anySignal([opts.signal, timeout]) : timeout;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      cache: 'no-store',
      signal,
    });
  } catch (err) {
    if (opts.signal?.aborted) throw err;
    const timedOut = err instanceof DOMException && err.name === 'TimeoutError';
    throw new ApiError(
      timedOut ? 'The request timed out. The server may be busy — try again.' : 'Cannot reach the API. Check that the backend is running.',
      0,
      timedOut ? 'timeout' : 'network',
    );
  }
  const requestId = res.headers.get('x-request-id') ?? undefined;
  if (opts.raw && res.ok) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let body: unknown;
  try { body = text ? JSON.parse(text) : undefined; } catch { body = undefined; }
  if (!res.ok) throw parseError(res.status, body, requestId);
  if (body === undefined && text) throw new ApiError('The API returned an invalid response.', res.status, 'bad_response', {}, requestId);
  return body as T;
}

// AbortSignal.any is not available in every supported browser; combine manually.
function anySignal(signals: AbortSignal[]): AbortSignal {
  const ctrl = new AbortController();
  for (const s of signals) {
    if (s.aborted) { ctrl.abort(s.reason); break; }
    s.addEventListener('abort', () => ctrl.abort(s.reason), { once: true });
  }
  return ctrl.signal;
}

export async function downloadFile(path: string, body: unknown, fallbackName: string): Promise<void> {
  const res = await request<Response>(path, { method: 'POST', body, raw: true, timeoutMs: 120_000 });
  const disposition = res.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
